import { type FormEvent, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getBusinessMetrics } from '../api/endpoints/metrics';
import type { BookingStatusCounts } from '../api/schemas';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { BOOKING_STATUS_LABEL } from '../lib/bookings';
import { formatMoneyCents } from '../lib/format';
import { computeAckRate, dateInputToRange, formatAckRatePercent, totalBookings } from '../lib/metrics';

/**
 * Display order for the status breakdown; mirrors the backend's own tally
 * shape (`BookingStatusCounts`, a fixed set that deliberately does not carry
 * `no_show` — see `BusinessCustomerSummary.noShowCount` for that number).
 */
const STATUS_ORDER: (keyof BookingStatusCounts)[] = [
  'held',
  'confirmed',
  'completed',
  'paid',
  'released',
  'cancelled',
];

export function HistorialPage() {
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [range, setRange] = useState<{ from?: string; to?: string }>({});

  const metricsQuery = useQuery({
    queryKey: ['business-metrics', range.from, range.to],
    queryFn: () => getBusinessMetrics(range.from, range.to),
  });

  const ackRate = useMemo(
    () => (metricsQuery.data ? computeAckRate(metricsQuery.data.cues) : undefined),
    [metricsQuery.data],
  );

  function applyRange(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setRange(dateInputToRange(fromInput || undefined, toInput || undefined));
  }

  const data = metricsQuery.data;
  const hasBookings = data ? totalBookings(data.bookings) > 0 : false;

  return (
    <main>
      <PageHeader title="Historial" description="Reservas, cumplimiento y confirmaciones de preferencia." />
      <form onSubmit={applyRange} aria-label="Rango de fechas" className="filter-row">
        <div className="field">
          <label className="field-label" htmlFor="historial-desde">
            Desde
          </label>
          <input
            id="historial-desde"
            type="date"
            value={fromInput}
            onChange={(event) => setFromInput(event.target.value)}
          />
        </div>
        <div className="field">
          <label className="field-label" htmlFor="historial-hasta">
            Hasta
          </label>
          <input
            id="historial-hasta"
            type="date"
            value={toInput}
            onChange={(event) => setToInput(event.target.value)}
          />
        </div>
        <button type="submit" className="btn small secondary">
          Aplicar
        </button>
      </form>
      {metricsQuery.isLoading ? <LoadingState label="Cargando historial…" /> : null}
      {metricsQuery.isError ? <ErrorBanner message={errorMessage(metricsQuery.error)} /> : null}
      {data ? (
        <>
          {/* Cues are tallied on their own `shownAt` window, independent of
              whether their booking's `startsAt` falls in range, so this note
              only replaces the status breakdown, never the summary below. */}
          <section aria-label="Resumen" className="section">
            <h2 className="section-label">Resumen</h2>
            <div className="card rows">
              <div className="row">
                <span className="grow">Completadas</span>
                <span className="amount">{data.completions}</span>
              </div>
              <div className="row">
                <span className="grow">Ingresos</span>
                <span className="amount">{formatMoneyCents(data.revenueCents)}</span>
              </div>
              <div className="row">
                <span className="grow">Confirmaciones de preferencia</span>
                <span className="amount">
                  {data.cues.acknowledged} de {data.cues.shown} ({formatAckRatePercent(ackRate)})
                </span>
              </div>
            </div>
          </section>
          <section aria-label="Reservas por estado" className="section">
            <h2 className="section-label">Reservas por estado</h2>
            {!hasBookings ? (
              <EmptyState title="No hay reservas en ese rango." />
            ) : (
              <ul className="rows">
                {STATUS_ORDER.map((status) => (
                  <li key={status} className="row">
                    <span className="grow">{BOOKING_STATUS_LABEL[status]}</span>
                    <span className="amount">{data.bookings[status]}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      ) : null}
    </main>
  );
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos cargar el historial.';
}
